import { Avatar, Flex, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';

import NoImage from 'assets/no-image.png';
import CustomLoader from 'components/CustomLoader';

import useFarmerPgs from 'screens/farmer/hooks/useFarmerPgs';
import useFarmerQuery from 'screens/farmer/hooks/useFarmerQuery';
import useSharedPhoneNumberQuery from 'screens/farmer/hooks/useSharedPhoneNumberQuery';

import { Farmer } from 'types/common';
import { renderInfoValue } from 'utils/helpers';
import PersonalDetailsTab from './PersonalDetailsTab';
import PlantingGroupsTab from './PlantingGroupTab';

type TFarmer = Farmer & {
  role?: string;
};

type FarmerDrawerContentProps = {
  data?: TFarmer;
  showFGLData: boolean;
  setShowFGLData: (show: boolean) => void;
};

const FarmerDrawerContent: React.FC<FarmerDrawerContentProps> = ({
  data,
  showFGLData,
  setShowFGLData,
}: FarmerDrawerContentProps) => {
  const farmerId = data?.id;

  const [activeTab, setActiveTab] = useState('1');

  const {
    data: farmerInfo,
    isLoading: isLoadingDocuments,
    prefetch: prefetchFarmer,
  } = useFarmerQuery(farmerId);

  const {
    pgOptions,
    selectedSeason,
    setSelectedSeason,
    currentSeason,
    loading: isPgLoading,
  } = useFarmerPgs({ farmerId });

  const { data: farmersSharedPhoneNumber } = useSharedPhoneNumberQuery(
    data?.phoneNumber,
    farmerId,
  );

  useEffect(() => {
    setActiveTab('1');
  }, [farmerId]);

  const tabItems = [
    {
      key: '1',
      label: 'Personal Details',
      children: (
        <PersonalDetailsTab
          data={data}
          farmerInfo={farmerInfo}
          isLoadingDocuments={isLoadingDocuments}
          prefetchFarmer={prefetchFarmer}
          farmersSharedPhoneNumber={farmersSharedPhoneNumber}
          showFGLData={showFGLData}
          setShowFGLData={setShowFGLData}
        />
      ),
    },
    {
      key: '2',
      label: 'Planting Groups',
      children: isPgLoading ? (
        <CustomLoader size="small" />
      ) : (
        <PlantingGroupsTab
          pgOptions={pgOptions}
          selectedSeason={selectedSeason}
          setSelectedSeason={setSelectedSeason}
          currentSeason={currentSeason}
        />
      ),
    },
  ];

  return (
    <>
      <Flex
        gap={10}
        align="center"
        style={{
          border: '1px solid #e8e8e8',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        {data?.profileImage ? (
          <PhotoProvider>
            <PhotoView src={data?.profileImage}>
              <img
                src={data?.profileImage}
                alt="Farmer Profile"
                className="img-fluid me-2 farmer-profile"
              />
            </PhotoView>
          </PhotoProvider>
        ) : (
          <Avatar src={NoImage} size={44} />
        )}
        <Flex vertical>
          <Typography.Title className="m-0" level={5}>
            {renderInfoValue(data?.name)}
          </Typography.Title>
          <Typography.Text className="info-label">
            {renderInfoValue(data?.groupName)}
          </Typography.Text>
        </Flex>
      </Flex>
      <Tabs
        className="stakeholder-tabs"
        items={tabItems}
        defaultActiveKey="1"
        activeKey={activeTab}
        onTabClick={setActiveTab}
      />
    </>
  );
};

export default FarmerDrawerContent;
